import { Grid, Skeleton } from '@mui/material';
import { ReactComponent as Celebratory } from '../shared/icons/Celebratory.svg';

import {
	CerebroBreadcrumb,
	CerebroHeading,
	CerebroIcon,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroStyledBox,
	CerebroText,
	CerebroSummary,
} from '../shared/components';
import { Deal, isDealActive, isDealPending } from '../shared/models';
import { useDealProductQuery, useDealQuery } from '../shared/hooks';
import { useParams, useNavigate } from 'react-router-dom';
import { LoanKickOffUrl } from '../shared/constants';
import { PlatformRootUrl } from '../shared/utils/platform';
import { useAuth, Urls } from '../shared/context';

export const Results: React.FC = () => {
	const { guid } = useParams<'guid'>();
	const dealQuery = useDealQuery(guid);
	const productQuery = useDealProductQuery(guid);
	const auth = useAuth();

	const isResultsPageActive =
		(productQuery.data === undefined ||
			productQuery.data?.isContractRequired ||
			(productQuery.data !== undefined &&
				!productQuery.data?.isContractRequired &&
				dealQuery.data?.dealStatus !== 'Accepted')) &&
		dealQuery.data?.dealStatus !== 'Started';

	const navigate = useNavigate();

	const openDataRoom = (deal: Deal | null | undefined) => {
		if (!deal) return;

		const url = `${PlatformRootUrl}/Emp/ManageDataRoom/${deal.dataRoomGuid}`;
		window.location.href = url;
	};

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Summary</CerebroBreadcrumb>

			{dealQuery.isLoading && <Skeleton height={400} />}

			{!auth.currentUser?.isEmployeeUser && !isResultsPageActive ? (
				<>
					<CerebroText sx={{ marginTop: 3, marginBottom: 6 }}>
						Your loan application has not been submitted.
					</CerebroText>

					<CerebroPrimaryButton
						onClick={() => navigate(`/${guid}/${Urls.Review.Root}`)}
						sx={{ marginBottom: 6 }}
					>
						Continue
					</CerebroPrimaryButton>
				</>
			) : (
				<>
					{!dealQuery.isLoading && isDealPending(dealQuery.data) && (
						<>
							<Grid justifyContent={'center'} alignItems={'center'} container>
								<Grid item xs={12} md={4}>
									<CerebroIcon sx={{ paddingRight: 1 }}>
										<Celebratory width="266" height="266" />
									</CerebroIcon>
								</Grid>
								<Grid item xs={12} md={8}>
									<CerebroHeading size="h1" component="h1" sx={{ marginBottom: 3 }}>
										Thank You!
									</CerebroHeading>
									<CerebroText sx={{ marginBottom: 2 }}>
										Your application has been received!
									</CerebroText>
									<CerebroText sx={{ marginBottom: 2 }}>
										With this one application, you will match with the best lenders for your
										specific situation.
									</CerebroText>
									<CerebroText sx={{ marginBottom: 2 }}>
										Our experts will contact you so we can help you get your loan funded quickly.
									</CerebroText>
								</Grid>
							</Grid>

							<CerebroStyledBox sx={{ marginBottom: 8 }}>
								<CerebroHeading size="h2" component="h2" textAlign={'center'}>
									What&apos;s Next?
								</CerebroHeading>
								<br />
								<CerebroText textAlign={'center'}>
									Schedule a kick-off call with your Cerebro team who will help you get your loan
									funded.
								</CerebroText>
								<br />
								<br />
								<CerebroPrimaryButton wide target="_blank" href={LoanKickOffUrl} textAlign={'center'}>
									Schedule Kick-off Call
								</CerebroPrimaryButton>
								<br />
								<br />
								<CerebroHeading size="h2" component="h2" textAlign={'center'}>
									Questions? Call us at (240) 394-1311 or email{' '}
									<a href="mailto:sales@cerebrocapital.com">sales@cerebrocapital.com</a>
								</CerebroHeading>
							</CerebroStyledBox>
						</>
					)}

					{!dealQuery.isLoading && isDealActive(dealQuery.data) && (
						<>
							<CerebroHeading>
								Congratulations! Lenders are being invited to bid on your request.
							</CerebroHeading>
							<CerebroText sx={{ marginTop: 3, marginBottom: 6 }}>
								Click below to review activity in your data room.
							</CerebroText>

							<CerebroPrimaryButton onClick={() => openDataRoom(dealQuery.data)} sx={{ marginBottom: 6 }}>
								Access Data Room
							</CerebroPrimaryButton>

							<CerebroSummary showLinks={true} compressView={false} />
						</>
					)}
				</>
			)}
		</CerebroPageContainer>
	);
};
